const textEncoder = new TextEncoder();

export const utf8 = (value: string): Uint8Array => textEncoder.encode(value);

export const concatBytes = (...values: readonly Uint8Array[]): Uint8Array => {
  const result = new Uint8Array(
    values.reduce((sum, value) => sum + value.length, 0)
  );
  let offset = 0;
  for (const value of values) {
    result.set(value, offset);
    offset += value.length;
  }
  return result;
};

export const bytesToHex = (value: Uint8Array): string =>
  [...value].map((byte) => byte.toString(16).padStart(2, "0")).join("");

export const hexToBytes = (
  value: string,
  expectedBytes?: number
): Uint8Array => {
  if (
    value.length % 2 !== 0 ||
    !/^[0-9a-f]*$/.test(value) ||
    (expectedBytes !== undefined && value.length !== expectedBytes * 2)
  ) {
    throw new Error("Invalid canonical lowercase hexadecimal value");
  }
  return Uint8Array.from({ length: value.length / 2 }, (_, index) =>
    Number.parseInt(value.slice(index * 2, index * 2 + 2), 16)
  );
};

export const base64Encode = (value: Uint8Array): string => {
  let binary = "";
  for (const byte of value) binary += String.fromCharCode(byte);
  return btoa(binary);
};

export const base64Decode = (value: string): Uint8Array => {
  const binary = atob(value);
  return Uint8Array.from(binary, (character) => character.charCodeAt(0));
};

export class CanonicalEncoder {
  private readonly chunks: Uint8Array[] = [];

  pushU16(value: number): this {
    if (!Number.isInteger(value) || value < 0 || value > 0xffff) {
      throw new Error("u16 is out of range");
    }
    const bytes = new Uint8Array(2);
    new DataView(bytes.buffer).setUint16(0, value, false);
    this.chunks.push(bytes);
    return this;
  }

  pushU32(value: number): this {
    if (!Number.isInteger(value) || value < 0 || value > 0xffffffff) {
      throw new Error("u32 is out of range");
    }
    const bytes = new Uint8Array(4);
    new DataView(bytes.buffer).setUint32(0, value, false);
    this.chunks.push(bytes);
    return this;
  }

  pushU64(value: bigint): this {
    return this.pushUnsigned(value, 8);
  }

  pushU128(value: bigint): this {
    return this.pushUnsigned(value, 16);
  }

  pushField(value: string): this {
    this.chunks.push(hexToBytes(value, 32));
    return this;
  }

  pushBytes(value: Uint8Array): this {
    this.pushU32(value.length);
    this.chunks.push(value);
    return this;
  }

  pushString(value: string): this {
    return this.pushBytes(utf8(value));
  }

  finish(): Uint8Array {
    return concatBytes(...this.chunks);
  }

  private pushUnsigned(value: bigint, bytesLength: number): this {
    if (value < 0n || value >= 1n << BigInt(bytesLength * 8)) {
      throw new Error(`u${bytesLength * 8} is out of range`);
    }
    const bytes = new Uint8Array(bytesLength);
    let remaining = value;
    for (let index = bytesLength - 1; index >= 0; index -= 1) {
      bytes[index] = Number(remaining & 0xffn);
      remaining >>= 8n;
    }
    this.chunks.push(bytes);
    return this;
  }
}
